import {
  CategoriesTiles as CategoriesTilesLayout,
  type TileItem,
} from '@workspace/shared/components/CategoriesTiles';
import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import CategoryBlockTitle from '~/components/CategoryBlocks/CategoryBlockTitle';
import {
  cfSrcForImage,
  createCategoryUrl,
} from '~/components/CategoryBlocks/utils';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { catalogStore } from '~/stores';
import type { Category } from '~/stores/CategoriesStore';

const TILE_IMAGE_OPTIONS = {
  fit: 'contain' as const,
  width: 512,
  height: 484,
};

interface CategoriesTilesProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
  level?: 2 | 3;
}

const CategoriesTiles: React.FC<CategoriesTilesProps> = ({
  category,
  level = 2,
  ...props
}) => {
  const { handleClickCategory } = useCategoryAnalytics();

  const subcategories =
    level === 3
      ? category.subcategory.flatMap((c) => c.subcategory)
      : category.subcategory;

  const parentIdByItemId = new Map(
    subcategories.map((s) => [s.id, s.parentId]),
  );

  const items: TileItem[] = subcategories
    .filter((c) => c.parentId != null)
    .map(({ id, name, previewImage }) => ({
      id,
      title: name,
      imageUrl: previewImage
        ? cfSrcForImage(previewImage, TILE_IMAGE_OPTIONS)
        : undefined,
      imageAlt: name,
    }));

  return (
    <CategoriesTilesLayout
      {...props}
      items={items}
      renderTileWrapper={(item, tile) => (
        <Link
          to={createCategoryUrl({ id: item.id })}
          state={{ clean_filters: true }}
          className="no-underline hover:no-underline"
          onClick={() => {
            const parentId = parentIdByItemId.get(item.id);
            if (parentId) handleClickCategory(parentId)();
            catalogStore.resetCatalogFilters();
          }}
        >
          {tile}
        </Link>
      )}
    >
      <CategoriesTilesLayout.Header>
        <CategoryBlockTitle
          text={category.name}
          linkTo={createCategoryUrl({ id: category.id })}
          categoryId={category.id}
          hasLink
        />
      </CategoriesTilesLayout.Header>
    </CategoriesTilesLayout>
  );
};

export default observer(CategoriesTiles);
